import React, { createContext, useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from './AuthContext';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import DownloadIcon from '@mui/icons-material/Download';

const JobContext = createContext(null);
const normalizeJobId = (value) => String(value ?? '');

const getReportEndpoint = (type) => {
  switch (type) {
    case 'contact': return '/contacts/report';
    case 'pratiche_summary': return '/reports/pratiche-summary';
    case 'pratiche_legal': return '/reports/pratiche-legal';
    case 'pratiche_monitoraggio': return '/reports/pratiche-monitoring';
    case 'clienti_monitoraggio': return '/reports/clienti-monitoring';
    case 'pratiche_carichi_lavoro_report': return '/reports/pratiche-carichi-lavoro';
    case 'pratiche_carichi_lavoro_legal_detail_report': return '/reports/pratiche-carichi-lavoro-legal-detail';
    case 'pratiche_carichi_contabile_report': return '/reports/pratiche-carichi-contabile';
    case 'pratiche_carichi_contabile_detail_report': return '/reports/pratiche-carichi-contabile-detail';
    case 'pratiche_uo_report': return '/reports/pratiche-uo';
    case 'firme_report': return '/reports/firme';
    case 'firme_csv_report': return '/reports/firme-csv';
    case 'legali_chiusure_report': return '/reports/legali-chiusure';
    case 'legali_chiusure_csv_report': return '/reports/legali-chiusure-csv';
    case 'hr_absence_report': return '/reports/hr-assenze';
    case 'hr_early_entry_report': return '/reports/hr-ingressi-anticipati';
    case 'hr_time_variance_report': return '/reports/hr-anticipi-posticipi';
    case 'blacklist_segnalazioni_report': return '/reports/blacklist-segnalazioni';
    case 'passp_posizione_contabile_report': return '/reports/passp-posizione-contabile';
    case 'tipo_rapporto': return '/tipi-rapporto/report';
    case 'tipo_evento': return '/tipi-evento/report';
    case 'stato_pratica': return '/stati-pratica/report';
    case 'tipo_pratica': return '/tipi-pratica/report';
    case 'admin_activity_summary': return '/attivita/registro/report';
    case 'cloudtalk_sync': return '/cloudtalk/sync';
    case 'open_banking_tx_sync': return '/open-banking/transactions/sync-recent';
    case 'passp_fatture_sync': return '/passp-fatture/sync';
    case 'passp_fatture_bulk_send_email': return '/passp-fatture/bulk/send-email';
    case 'passp_clienti_sync': return '/passp-clientes/sync';
    case 'passp_conti_sync': return '/passp-conti/sync';
    case 'passp_prima_nota_sync': return '/passp-prima-nota/sync';
    case 'passp_client_statement': return '/passp-clientes/statement';
    case 'open_banking_statement': return '/open-banking/transactions/statement';
    case 'cloudtalk_calls': return '/cloudtalk/report';
    case 'customer_csv': return '/customer/export';
    case 'cloudtalk_recall_csv': return '/dossier/recall/export';
    case 'dossier_recall_csv': return '/dossier/recall/export';
    case 'negotiation_csv': return '/negotiation/export';
    case 'passp_situazione_contabile_csv': return '/passp-esposizione-contabile/situazione-contabile/export';
    case 'transazioni_calcolo_workflow_pdf': return '/negotiation/transazioni-calcolo/report-job';
    case 'funnelix_verifica_clienti_csv': return '/funnelix/verifica-clienti/job';
    case 'funnelix_push_clienti': return '/funnelix/clienti/push/job';
    case 'scadenzario_import_csv': return '/scadenzario/piani/importa';
    case 'scadenzario_performance_referenti_pdf': return '/scadenzario/kpi/performance-referenti/report-job';
    case 'kpi_lead_yearly_pdf': return '/kpi-lead/appuntamenti/report-job';
    case 'funnelix_import_sync': return '/funnelix/import/contatti/job';
    case 'open_banking_tx_csv_import': return '/open-banking/transactions-csv/import';
    case 'pf_clienti_assign': return '/pf-clienti/assign';
    case 'invii_modulistica_bulk_send_email': return '/invii-modulistica/bulk/send-email';
    case 'recessi_import_csv': return '/recessi/import-csv';
    case 'company':
    case 'report':
    default: return '/companies/report';
  }
};

const extractFilenameFromDisposition = (disposition) => {
  if (!disposition) return null;
  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(disposition);
  if (utf8Match && utf8Match[1]) return decodeURIComponent(utf8Match[1]);
  const plainMatch = /filename="?([^";]+)"?/i.exec(disposition);
  return plainMatch && plainMatch[1] ? plainMatch[1] : null;
};

const mapEntityTypeForLog = (type) => {
  if (type === 'hr_absence_report') return 'Report Assenze';
  if (type === 'hr_early_entry_report') return 'Report Ingressi Anticipati';
  if (type === 'hr_time_variance_report') return 'Report Anticipi/Posticipi';
  if (type === 'blacklist_segnalazioni_report') return 'Report Segnalazioni Blacklist';
  return type;
};

const mapEntityLabelForLog = (type, result) => {
  if (type === 'hr_absence_report') return 'Report Assenze';
  if (type === 'hr_early_entry_report') return 'Report Ingressi Anticipati';
  if (type === 'hr_time_variance_report') return 'Report Anticipi/Posticipi';
  if (type === 'blacklist_segnalazioni_report') return 'Report Segnalazioni Blacklist';
  if (result && typeof result.accountName === 'string') return result.accountName;
  return null;
};

const DownloadToast = ({ jobId, type, message, closeToast }) => {
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const endpoint = getReportEndpoint(type);
      const response = await api.get(`${endpoint}/${jobId}/download`, {
        responseType: 'blob',
      });
      console.log('Download response size:', response.data.size || response.data.length);
      const isCsv =
        type === 'customer_csv' ||
        type === 'cloudtalk_recall_csv' ||
        type === 'dossier_recall_csv' ||
        type === 'negotiation_csv' ||
        type === 'passp_situazione_contabile_csv' ||
        type === 'funnelix_verifica_clienti_csv' ||
        type === 'firme_csv_report' ||
        type === 'legali_chiusure_csv_report' ||
        type === 'funnelix_import_sync';
      const isTxt = type === 'scadenzario_import_csv';
      const mimeType = isCsv ? 'text/csv' : isTxt ? 'text/plain' : 'application/pdf';
      const blob = new Blob([response.data], { type: mimeType });
      console.log('Blob size:', blob.size);
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      const extension = isCsv ? 'csv' : isTxt ? 'txt' : 'pdf';
      const headerFilename = extractFilenameFromDisposition(response.headers?.['content-disposition']);
      link.setAttribute('download', headerFilename || `${type}_report_${jobId}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      if (closeToast) closeToast();
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Errore durante il download del file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
        {type === 'cloudtalk_recall_csv' && typeof message === 'string'
          ? message
          : type === 'dossier_recall_csv' && typeof message === 'string'
          ? message
          : type === 'kpi_lead_yearly_pdf' && typeof message === 'string'
          ? message
          : type === 'customer_csv' || type === 'cloudtalk_recall_csv' || type === 'dossier_recall_csv' || type === 'funnelix_verifica_clienti_csv' || type === 'firme_csv_report' || type === 'legali_chiusure_csv_report'
          ? 'File CSV pronto'
          : (type === 'open_banking_statement' || type === 'cloudtalk_calls' || type === 'hr_absence_report' || type === 'hr_early_entry_report' || type === 'hr_time_variance_report' || type === 'blacklist_segnalazioni_report') &&
            typeof message === 'string'
          ? message
          : 'Report pronto'}
      </Typography>
      <Button 
        variant="contained" 
        size="small" 
        color="primary" 
        startIcon={<DownloadIcon />}
        onClick={handleDownload}
        disabled={downloading}
        fullWidth
        sx={{ textTransform: 'none' }}
      >
        {downloading ? 'Scaricamento...' : 'Scarica file'}
      </Button>
    </Box>
  );
};

export const JobProvider = ({ children }) => {
  const [jobs, setJobs] = useState([]);
  const { user } = useAuth();

  const downloadJob = async (jobId) => {
    try {
      const normalizedJobId = normalizeJobId(jobId);
      const job = jobs.find((j) => normalizeJobId(j.id) === normalizedJobId);
      const type = job ? job.type : 'report';
      const endpoint = getReportEndpoint(type);
      
      const response = await api.get(`${endpoint}/${normalizedJobId}/download`, {
        responseType: 'blob',
      });
      console.log('Download response size:', response.data.size || response.data.length);
      const isCsv =
        type === 'customer_csv' ||
        type === 'cloudtalk_recall_csv' ||
        type === 'dossier_recall_csv' ||
        type === 'negotiation_csv' ||
        type === 'passp_situazione_contabile_csv' ||
        type === 'funnelix_verifica_clienti_csv' ||
        type === 'firme_csv_report' ||
        type === 'legali_chiusure_csv_report' ||
        type === 'funnelix_import_sync';
      const isTxt = type === 'scadenzario_import_csv';
      const mimeType = isCsv ? 'text/csv' : isTxt ? 'text/plain' : 'application/pdf';
      const blob = new Blob([response.data], { type: mimeType });
      console.log('Blob size:', blob.size);
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      const extension = isCsv ? 'csv' : isTxt ? 'txt' : 'pdf';
      const headerFilename = extractFilenameFromDisposition(response.headers?.['content-disposition']);
      link.setAttribute('download', headerFilename || `${type}_report_${normalizedJobId}.${extension}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Errore durante il download del file');
      return false;
    }
  };

  // Add a new job to track
  const addJob = (jobId, type = 'report', options = {}) => {
    const { silent = false, meta = null } = options;
    const normalizedJobId = normalizeJobId(jobId);
    // Check if job already exists to prevent duplicates
    if (!jobs.find((j) => normalizeJobId(j.id) === normalizedJobId)) {
      setJobs((prev) => [
        ...prev,
        {
          id: normalizedJobId,
          type,
          status: 'active',
          createdAt: new Date(),
          completedAt: null,
          durationMs: null,
          progress: 0,
          read: false,
          userId: user?.id || null,
          userName: user?.name || null,
          meta: meta && typeof meta === 'object' ? meta : null,
        },
      ]);
      
      const message =
        type === 'cloudtalk_sync' ||
        type === 'open_banking_tx_sync' ||
        type === 'passp_fatture_sync' ||
        type === 'passp_clienti_sync' ||
        type === 'passp_conti_sync' ||
        type === 'passp_prima_nota_sync'
          ? 'Sincronizzazione avviata... il risultato sarà disponibile nelle notifiche'
          : type === 'pf_clienti_assign'
          ? 'Assegnazione clienti avviata... il risultato sarà disponibile nelle notifiche'
          : type === 'scadenzario_import_csv'
          ? 'Importazione piani avviata... il risultato sarà disponibile nelle notifiche'
          : type === 'scadenzario_performance_referenti_pdf'
          ? 'Generazione Report Performance Referenti avviata... il file sarà disponibile nelle notifiche.'
          : type === 'kpi_lead_yearly_pdf'
          ? 'Generazione Report KPI Lead Annuale avviata... il file sarà disponibile nelle notifiche.'
          : type === 'transazioni_calcolo_workflow_pdf'
          ? 'Generazione Report Transazioni (Workflow) avviata... il file sarà disponibile nelle notifiche.'
          : type === 'hr_absence_report'
          ? 'Generazione report assenze avviata... il file sarà disponibile nelle notifiche.'
          : type === 'passp_posizione_contabile_report'
          ? 'Generazione report posizione contabile avviata... il file sarà disponibile nelle notifiche.'
          : type === 'funnelix_import_sync'
          ? 'Import Funnelix avviato... il risultato sarà disponibile nelle notifiche'
          : type === 'open_banking_tx_csv_import'
          ? 'Importazione transazioni CSV avviata... il riepilogo sarà disponibile nelle notifiche'
          : type === 'funnelix_verifica_clienti_csv'
          ? 'Verifica clienti Funnelix avviata... il CSV sarà disponibile nelle notifiche'
          : type === 'funnelix_push_clienti'
          ? 'Aggiornamento clienti su Funnelix avviato... il risultato sarà disponibile nelle notifiche'
          : type === 'invii_modulistica_bulk_send_email'
          ? 'Invio email avviato... l’esito sarà disponibile nelle notifiche'
          : type === 'passp_fatture_bulk_send_email'
          ? 'Invio email avviato... l’esito sarà disponibile nelle notifiche'
          : type === 'recessi_import_csv'
          ? 'Import recessi avviato... l’esito sarà disponibile nelle notifiche'
          : type === 'negotiation_csv'
          ? 'Esportazione CSV pratiche avviata... il file sarà disponibile nelle notifiche'
          : type === 'passp_situazione_contabile_csv'
          ? 'Esportazione CSV Situazione Clienti avviata... il file sarà disponibile nelle notifiche'
          : type === 'firme_csv_report'
          ? 'Esportazione CSV firme avviata... il file sarà disponibile nelle notifiche'
          : type === 'admin_activity_summary'
          ? 'Generazione report attività avviata... il PDF sarà disponibile nelle notifiche.'
          : type === 'legali_chiusure_report'
          ? 'Generazione report legali chiusure avviata... il file sarà disponibile nelle notifiche'
          : type === 'legali_chiusure_csv_report'
          ? 'Esportazione CSV legali chiusure avviata... il file sarà disponibile nelle notifiche'
          : type === 'pratiche_uo_report'
          ? 'Generazione report pratiche U/O avviata... il file sarà disponibile nelle notifiche'
          : type === 'pratiche_carichi_lavoro_report'
          ? 'Generazione report carichi per legale avviata... il PDF sarà disponibile nelle notifiche.'
          : 'Generazione report avviata. Alla fine il report sarà disponibile nelle notifiche.';

      if (!silent) {
        toast.info(message, { 
          position: 'top-right',
          autoClose: 3000
        });
      }
    }
  };

  // Add a generic monitoring notification (no polling, no download)
  const addMonitoringNotification = (info) => {
    const id = `monitoring-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    setJobs((prev) => [
      ...prev,
      {
        id,
        type: 'monitoring',
        status: 'completed',
        createdAt: new Date(),
        read: false,
        info: info || {}
      }
    ]);
  };

  const markAsRead = (jobId) => {
    const normalizedJobId = normalizeJobId(jobId);
    setJobs((prev) =>
      prev.map((j) =>
        normalizeJobId(j.id) === normalizedJobId ? { ...j, read: true } : j
      )
    );
  };
  
  const markAllAsRead = () => {
    setJobs(prev => prev.map(j => ({ ...j, read: true })));
  };
  
  const removeJob = (jobId) => {
    const normalizedJobId = normalizeJobId(jobId);
    setJobs((prev) =>
      prev.filter((j) => normalizeJobId(j.id) !== normalizedJobId)
    );
  };

  // Poll for job status
  useEffect(() => {
    // Only poll if there are active jobs
    const activeJobs = jobs.filter(j => j.status === 'active');
    if (activeJobs.length === 0) return;

    const intervalId = setInterval(async () => {
      
      const currentActiveJobs = jobs.filter(j => j.status === 'active');
      
      for (const job of currentActiveJobs) {
        try {
          const endpoint = getReportEndpoint(job.type);
          const res = await api.get(`${endpoint}/${job.id}`, {
            params: { _: Date.now() }
          });
          const { state, result } = res.data;
          const progress = res.data?.progress;

          if (state !== 'completed' && state !== 'failed') {
            const safeProgress = Number.isFinite(Number(progress)) ? Number(progress) : null;
            setJobs((prev) =>
              prev.map((j) => {
                if (normalizeJobId(j.id) !== normalizeJobId(job.id)) return j;
                if (safeProgress === null) return j;
                if (Number(j.progress) === safeProgress) return j;
                return { ...j, progress: safeProgress };
              })
            );
          }

          if (state === 'completed') {
            const finishedAt = new Date();
            // Mark as completed and store result + timing info
            setJobs((prev) =>
              prev.map((j) => {
                if (normalizeJobId(j.id) !== normalizeJobId(job.id)) return j;
                const created =
                  j.createdAt instanceof Date
                    ? j.createdAt
                    : new Date(j.createdAt || Date.now());
                const durationMs = finishedAt.getTime() - created.getTime();
                const updatedJob = {
                  ...j,
                  status: 'completed',
                  progress: 100,
                  result,
                  completedAt: finishedAt,
                  durationMs: Number.isFinite(durationMs) ? durationMs : null,
                };
                if (updatedJob.type !== 'funnelix_import_sync') {
                  try {
                    api.post('/job-logs', {
                      jobId: updatedJob.id,
                      type: updatedJob.type,
                      status: updatedJob.status,
                      message: result && typeof result.message === 'string' ? result.message : null,
                      entityType: mapEntityTypeForLog(updatedJob.type),
                      entityLabel: mapEntityLabelForLog(updatedJob.type, result),
                      userId: updatedJob.userId || null,
                      userName: updatedJob.userName || null,
                      createdAt: updatedJob.createdAt,
                      completedAt: updatedJob.completedAt,
                      durationMs: updatedJob.durationMs,
                      result,
                      error: null,
                    }).catch(() => {});
                  } catch (_) {}
                }
                return updatedJob;
              })
            );
            
            if (
              job.type === 'cloudtalk_sync' ||
              job.type === 'open_banking_tx_sync' ||
              job.type === 'passp_fatture_sync' ||
              job.type === 'passp_clienti_sync' ||
              job.type === 'passp_conti_sync' ||
              job.type === 'passp_prima_nota_sync' ||
              job.type === 'funnelix_import_sync' ||
              job.type === 'open_banking_tx_csv_import' ||
              job.type === 'funnelix_push_clienti' ||
              job.type === 'pf_clienti_assign'
            ) {
              let defaultMsg = 'Sincronizzazione completata';
              if (job.type === 'cloudtalk_sync') {
                defaultMsg = 'Sincronizzazione CloudTalk completata';
              } else if (job.type === 'open_banking_tx_sync') {
                defaultMsg = 'Sincronizzazione Open Banking completata';
              } else if (job.type === 'passp_fatture_sync') {
                defaultMsg = 'Sincronizzazione Fatture Passpartout completata';
              } else if (job.type === 'passp_clienti_sync') {
                defaultMsg = 'Sincronizzazione Clienti Passpartout completata';
              } else if (job.type === 'passp_conti_sync') {
                if (result && result.azienda && result.anno) {
                  defaultMsg = `Sincronizzazione Conti di Bilancio - Azienda ${result.azienda} / ${result.anno}`;
                } else {
                  defaultMsg = 'Sincronizzazione Conti di Bilancio completata';
                }
              } else if (job.type === 'passp_prima_nota_sync') {
                defaultMsg = 'Sincronizzazione Prima Nota Passpartout completata';
              } else if (job.type === 'funnelix_import_sync') {
                defaultMsg = 'Import Funnelix completato';
              } else if (job.type === 'open_banking_tx_csv_import') {
                defaultMsg = 'Importazione transazioni CSV completata';
              } else if (job.type === 'funnelix_push_clienti') {
                defaultMsg = 'Aggiornamento clienti su Funnelix completato';
              } else if (job.type === 'pf_clienti_assign') {
                defaultMsg = 'Assegnazione clienti completata';
              }
              const msg = result?.message || defaultMsg;
              toast.success(msg, { position: 'top-right', autoClose: 5000 });
            } else if (job.type === 'scadenzario_import_csv') {
              const failed = Number(result?.failed || 0);
              const msg = result?.message || 'Importazione piani completata';
              if (failed > 0) {
                toast.warning(
                  `${msg}. TXT esito disponibile in campanella.`,
                  { position: 'top-right', autoClose: 6000 }
                );
              } else {
                toast.success(`${msg}. TXT esito disponibile in campanella.`, {
                  position: 'top-right',
                  autoClose: 5000
                });
              }
            } else if (job.type === 'invii_modulistica_bulk_send_email') {
              const msg = result?.message || 'Invio email completato';
              toast.success(`${msg}. Esito disponibile in campanella.`, {
                position: 'top-right',
                autoClose: 5000
              });
            } else if (job.type === 'passp_fatture_bulk_send_email') {
              const msg = result?.message || 'Invio email completato';
              toast.success(`${msg}. Esito disponibile in campanella.`, {
                position: 'top-right',
                autoClose: 5000
              });
            } else if (job.type === 'recessi_import_csv') {
              const failed = Number(result?.failed || 0);
              const msg = result?.message || 'Import recessi completato';
              if (failed > 0) {
                toast.warning(`${msg}. Dettagli disponibili in campanella.`, { position: 'top-right', autoClose: 6000 });
              } else {
                toast.success(`${msg}.`, { position: 'top-right', autoClose: 5000 });
              }
            } else {
              // Show success notification with download button
              toast.success(
                <DownloadToast
                  jobId={job.id}
                  type={job.type}
                  message={result && typeof result.message === 'string' ? result.message : undefined}
                />,
                { 
                  // Keep it open until user interacts
                  autoClose: false, 
                  closeOnClick: false,
                  position: 'top-right',
                  icon: false // Hide default icon to save space for our custom UI
                }
              );
            }
          } else if (state === 'failed') {
            const failureReason =
              res.data?.failedReason ||
              res.data?.error ||
              (result && typeof result.message === 'string' ? result.message : null) ||
              null;
            const finishedAt = new Date();
            setJobs((prev) =>
              prev.map((j) => {
                if (normalizeJobId(j.id) !== normalizeJobId(job.id)) return j;
                const created =
                  j.createdAt instanceof Date
                    ? j.createdAt
                    : new Date(j.createdAt || Date.now());
                const durationMs = finishedAt.getTime() - created.getTime();
                const updatedJob = {
                  ...j,
                  status: 'failed',
                  progress: Number.isFinite(Number(j.progress)) ? Number(j.progress) : null,
                  completedAt: finishedAt,
                  durationMs: Number.isFinite(durationMs) ? durationMs : null,
                  error: failureReason,
                };
                if (updatedJob.type !== 'funnelix_import_sync') {
                  try {
                    api.post('/job-logs', {
                      jobId: updatedJob.id,
                      type: updatedJob.type,
                      status: updatedJob.status,
                      message: null,
                      entityType: mapEntityTypeForLog(updatedJob.type),
                      entityLabel: null,
                      userId: updatedJob.userId || null,
                      userName: updatedJob.userName || null,
                      createdAt: updatedJob.createdAt,
                      completedAt: updatedJob.completedAt,
                      durationMs: updatedJob.durationMs,
                      result: null,
                      error: failureReason,
                    }).catch(() => {});
                  } catch (_) {}
                }
                return updatedJob;
              })
            );
            const reason = failureReason ? `: ${failureReason}` : '';
            let label = 'Generazione report';
            if (
              job.type === 'customer_csv' ||
              job.type === 'cloudtalk_recall_csv' ||
              job.type === 'dossier_recall_csv' ||
              job.type === 'firme_csv_report' ||
              job.type === 'legali_chiusure_csv_report'
            ) {
              label = 'Esportazione';
            } else if (job.type === 'pf_clienti_assign') {
              label = 'Assegnazione clienti';
            } else if (job.type === 'scadenzario_import_csv') {
              label = 'Importazione piani';
            } else if (job.type === 'invii_modulistica_bulk_send_email') {
              label = 'Invio email';
            } else if (job.type === 'passp_fatture_bulk_send_email') {
              label = 'Invio email';
            } else if (
              job.type === 'cloudtalk_sync' ||
              job.type === 'open_banking_tx_sync' ||
              job.type === 'passp_fatture_sync' ||
              job.type === 'passp_clienti_sync' ||
              job.type === 'passp_conti_sync' ||
              job.type === 'passp_prima_nota_sync' ||
              job.type === 'funnelix_import_sync' ||
              job.type === 'open_banking_tx_csv_import'
            ) {
              label = 'Sincronizzazione';
            }
            toast.error(`${label} fallita (ID: ${job.id})${reason}`, { position: 'top-right' });
          }
        } catch (error) {
          console.error(`Error checking job ${job.id}:`, error);
        }
      }
    }, 3000); // Check every 3 seconds

    return () => clearInterval(intervalId);
  }, [jobs.filter((j) => j.status === 'active').length]);

  return (
    <JobContext.Provider
      value={{
        addJob,
        jobs,
        downloadJob,
        markAsRead,
        markAllAsRead,
        removeJob,
        addMonitoringNotification
      }}
    >
      {children}
    </JobContext.Provider>
  );
};

export const useJob = () => useContext(JobContext);
